import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import {
	cloneElement,
	createContext,
	isValidElement,
	type ReactNode,
	useContext,
} from "react";

export const BREADCRUMB_LINK_CLASS =
	"min-w-0 break-words text-sm text-muted-foreground transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

/** The shell supplies the workspace section; pages own their named ancestors. */
export const BreadcrumbPrefixContext = createContext<ReactNode[]>([]);

export const BreadcrumbCurrentContext = createContext<ReactNode>(null);

export function PageBreadcrumb({
	crumbs,
	current,
}: {
	crumbs: ReactNode[];
	current?: ReactNode;
}) {
	const prefix = useContext(BreadcrumbPrefixContext);
	const headerTitle = useContext(BreadcrumbCurrentContext);
	const currentPage = current ?? headerTitle;
	const trail = [...prefix, ...crumbs];
	if (!trail.length && !currentPage) return null;
	return (
		<nav aria-label="Breadcrumb" className="min-w-0 text-sm">
			<ol className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
				{trail.map((crumb, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: breadcrumb slots are positional and contain no local state
					<li key={i} className="flex min-w-0 max-w-full items-center gap-1.5">
						{i > 0 ? (
							<ChevronRight
								aria-hidden="true"
								className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50"
							/>
						) : null}
						{/* Ancestors must not inherit the router's fuzzy current-page match. */}
						{isValidElement<{ activeOptions?: { exact?: boolean } }>(crumb) &&
						crumb.type === Link
							? cloneElement(crumb, {
									activeOptions: { ...crumb.props.activeOptions, exact: true },
								})
							: crumb}
					</li>
				))}
				{currentPage ? (
					<li className="flex min-w-0 max-w-full items-center gap-1.5">
						{trail.length ? (
							<ChevronRight
								aria-hidden="true"
								className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50"
							/>
						) : null}
						<span
							aria-current="page"
							className="min-w-0 break-words text-muted-foreground"
						>
							{currentPage}
						</span>
					</li>
				) : null}
			</ol>
		</nav>
	);
}
